import type { OrderFilterOptions } from '@/library/OrderFilterOptionsHelper';
import { orderSearchDefaultFields, type OrderSearchDefaults, type OrderRefreshRate } from '@/library/OrderSettingsHelper';
import MultiSelectDropdown from '@/common/MultiSelectDropdown';
import { SingleSelectDropdown } from '@/common/SingleSelectDropdown/SingleSelectDropdown';
import SearchableMultiSelectDropdown from '@/common/SearchableMultiSelectDropdown/SearchableMultiSelectDropdown';
import type { OrdersPageType } from './OrdersPresentation';
import styles from './SearchDefaults.module.scss';

interface SearchDefaultsProps {
  currentPage: OrdersPageType;
  options: OrderFilterOptions;
  value: OrderSearchDefaults;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: OrderSearchDefaults) => void;
  disabled?: boolean;
  refreshRate?: OrderRefreshRate;
  // eslint-disable-next-line no-unused-vars
  onRefreshRateChange?: (rate: OrderRefreshRate) => void;
}

const refreshRateOptions = [
  { value: '1', label: '1 minute' },
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
];

const labels: Record<keyof OrderSearchDefaults, string> = {
  headTypes: 'Club',
  buildLocations: 'Build Location',
  shipFromLocations: 'Ship From Location',
  shipToLocations: 'Ship To Location',
  orderStatuses: 'Order Status',
  orderAccounts: 'Account',
  appUsers: 'Tour Rep',
};

export function SearchDefaults({ currentPage, options, value, onChange, disabled = false, refreshRate = 5, onRefreshRateChange }: SearchDefaultsProps) {
  return (
    <section className={styles.section} aria-label="Search Defaults">
      <h2>Search Defaults</h2>
      <div className={styles.fields}>
        {orderSearchDefaultFields[currentPage].map((field) => {
          let fieldOptions = options.orderStatuses;
          if (field === 'shipFromLocations' || field === 'shipToLocations') fieldOptions = options.buildLocations;
          else fieldOptions = options[field];
          const selections = value[field].flatMap((id) => {
            const option = fieldOptions.find((option) => option.value === id.toString());
            return option ? [option] : [];
          });
          const props = {
            label: labels[field],
            options: fieldOptions,
            value: selections,
            disabled,
            onChange: (selected: typeof selections) => onChange({ ...value, [field]: selected.map((option) => Number(option.value)) }),
          };
          return (
            <div key={field}>
              {field === 'appUsers' && <SearchableMultiSelectDropdown {...props} searchLabel="Search tour reps" dropdownWidth="100%" />}
              {field !== 'appUsers' && <MultiSelectDropdown {...props} />}
            </div>
          );
        })}
        <SingleSelectDropdown
          label="Refresh Rate"
          className={styles.refreshRate}
          rounded
          options={refreshRateOptions}
          value={String(refreshRate)}
          disabled={disabled}
          onChange={(selected) => {
            const rate = Number(selected);
            if (rate === 1 || rate === 5 || rate === 10) onRefreshRateChange?.(rate);
          }}
        />
      </div>
    </section>
  );
}
